import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Header from '../Header';
import AdminNav from './AdminNav';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';

const PAGE_SIZE = 20;

export default function AdminSuggestionsScreen() {
    const { authFetch } = useAuth();
    const [filter, setFilter] = useState('unreviewed');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(
        async (offset = 0) => {
            setError(null);
            setIsLoading(true);
            try {
                const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
                if (filter === 'unreviewed') params.set('reviewed', 'false');
                if (filter === 'reviewed') params.set('reviewed', 'true');
                const result = await authFetch(`/admin/suggestions?${params.toString()}`);
                if (offset === 0) setItems(result.items);
                else setItems((prev) => [...prev, ...result.items]);
                setTotal(result.total);
            } catch (err) {
                setError(err instanceof ApiError ? err.message : 'No se pudo cargar la lista de sugerencias.');
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, filter]
    );

    useEffect(() => {
        load(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const handleToggleReviewed = async (s) => {
        setBusyId(s.id);
        setError(null);
        try {
            const updated = await authFetch(`/admin/suggestions/${s.id}`, {
                method: 'PATCH',
                body: { reviewed: !s.reviewed },
            });
            if (filter === 'all') {
                setItems((prev) => prev.map((it) => (it.id === s.id ? updated : it)));
            } else {
                setItems((prev) => prev.filter((it) => it.id !== s.id));
                setTotal((t) => t - 1);
            }
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'La acción ha fallado.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <>
            <Header title="Sugerencias" showSettings={false} />
            <AdminNav />
            <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
                <TextField select label="Filtro" value={filter} onChange={(e) => setFilter(e.target.value)} size="small" sx={{ mb: 2, minWidth: 200 }}>
                    <MenuItem value="unreviewed">Sin revisar</MenuItem>
                    <MenuItem value="reviewed">Revisadas</MenuItem>
                    <MenuItem value="all">Todas</MenuItem>
                </TextField>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Stack spacing={2}>
                    {items.map((s) => (
                        <Card key={s.id} elevation={2}>
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box>
                                        <Typography variant="subtitle2">
                                            {s.userName || 'Anónimo'} {s.userEmail && `· ${s.userEmail}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(s.createdAt).toLocaleString()}
                                            {s.appVersion && ` · v${s.appVersion}`}
                                            {s.platform && ` · ${s.platform}`}
                                        </Typography>
                                    </Box>
                                    <Chip size="small" label={s.reviewed ? 'Revisada' : 'Sin revisar'} color={s.reviewed ? 'success' : 'warning'} />
                                </Stack>
                                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{s.message}</Typography>
                                <Button
                                    size="small"
                                    sx={{ mt: 1 }}
                                    disabled={busyId === s.id}
                                    onClick={() => handleToggleReviewed(s)}
                                >
                                    {s.reviewed ? 'Marcar como no revisada' : 'Marcar como revisada'}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                    {!isLoading && items.length === 0 && (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin sugerencias.</Typography>
                    )}
                </Stack>

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && items.length < total && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button onClick={() => load(items.length)}>Cargar más ({items.length}/{total})</Button>
                    </Box>
                )}
            </Container>
        </>
    );
}
